import React, { useEffect, useState } from 'react';
import {
  Typography,
  IconButton,
  Box,
  Button,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Paper
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { SiTether } from 'react-icons/si';
import { getWallets, initiateCryptoTransfer, processCryptoTransfer } from './apiWallets';
import Preloader from "./components/Preloader";
import { useParams, useNavigate } from 'react-router-dom';

export default function CryptoTransfer({ setSidebarOpen }) {
  const { sourceWalletId } = useParams();
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Crypto transfer state
  const [activeStep, setActiveStep] = useState(0);
  const [sourceWallet, setSourceWallet] = useState(null);
  const [amount, setAmount] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [network, setNetwork] = useState('TRC20');
  const [description, setDescription] = useState('');
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState('');
  const [transferResult, setTransferResult] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');

  const steps = ['Enter Details', 'Confirm Transfer', 'Complete'];

  useEffect(() => {
    getWallets()
      .then(data => {
        const walletsArr = (data && data.data && data.data.data) || [];
        setWallets(walletsArr);
        
        // If sourceWalletId is provided, find and set the source wallet
        if (sourceWalletId) {
          const foundWallet = walletsArr.find(w => w.id === sourceWalletId || w.wallet_number === sourceWalletId);
          if (foundWallet) {
            setSourceWallet(foundWallet);
            // Set default network based on currency
            const defaultNetwork = foundWallet.currency?.code === 'USDT' ? 'TRC20' : 
                                  foundWallet.currency?.code === 'USDC' ? 'POL' : 'TRC20';
            setNetwork(defaultNetwork);
          }
        }
      })
      .catch(err => {
        setError('Failed to load wallets');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [sourceWalletId]);

  // Helper functions
  function getFlag(code) {
    if (code === 'NGN') return '🇳🇬';
    if (code === 'USD') return '🇺🇸';
    if (code === 'USDT') return 'USDT';
    if (code === 'GBP') return '🇬🇧';
    return '💰';
  }

  function getSymbol(code) {
    if (code === 'NGN') return '₦';
    if (code === 'USD' || code === 'USDT') return '$';
    if (code === 'GBP') return '£';
    return '';
  }

  function getCurrencyCode(wallet) {
    if (!wallet) return 'N/A';
    
    if (wallet.provider_metadata) {
      try {
        const meta = JSON.parse(wallet.provider_metadata);
        if (meta.currency) return meta.currency;
      } catch {}
    }
    return wallet.currency?.code || wallet.currency?.name || 'N/A';
  }

  function isCryptoWallet(wallet) {
    const cryptoCurrencies = ['BTC', 'ETH', 'USDT', 'USDC', 'BNB', 'ADA', 'DOT', 'LINK', 'LTC', 'BCH', 'XRP'];
    return cryptoCurrencies.includes(wallet.currency?.code);
  }

  const handleNext = () => {
    if (activeStep === 0) {
      if (!amount || parseFloat(amount) <= 0) {
        setTransferError('Please enter a valid amount');
        return;
      }
      if (!destinationAddress || destinationAddress.trim() === '') {
        setTransferError('Please enter a valid destination address');
        return;
      }
      if (!network) {
        setTransferError('Please select a network');
        return;
      }
      
      // Check if user has sufficient balance
      const sourceBalance = parseFloat(sourceWallet.balance || 0);
      if (parseFloat(amount) > sourceBalance) {
        setTransferError(`Insufficient balance. You have ${sourceBalance} ${sourceWallet.currency?.code}`);
        return;
      }
      
      setActiveStep(1);
      setTransferError('');
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setTransferError('');
  };

  const handleTransfer = async () => {
    if (!pin || pin.length !== 4) {
      setTransferError('Please enter a valid 4-digit transaction PIN');
      return;
    }
    
    setTransferLoading(true);
    setTransferError('');
    
    try {
      // First, initiate the crypto transfer
      const initiateRes = await initiateCryptoTransfer({
        source_wallet_id: sourceWallet.id,
        destination_address_code: destinationAddress.trim(),
        destination_address_network: network,
        amount: parseFloat(amount),
        description: description || `Crypto transfer to ${destinationAddress}`
      });
      
      if (initiateRes.status && initiateRes.data) {
        // Then process the crypto transfer with transaction pin
        const processRes = await processCryptoTransfer({
          source_wallet_id: sourceWallet.id,
          destination_address_code: destinationAddress.trim(),
          destination_address_network: network,
          amount: parseFloat(amount),
          description: description || `Crypto transfer to ${destinationAddress}`,
          transaction_pin: parseInt(pin)
        });
        
        if (processRes.status) {
          setTransferResult(processRes.data);
          setActiveStep(2);
          setSnackbarMsg('Crypto transfer initiated successfully!');
          setSnackbarOpen(true);
          
          // Refresh wallets
          const walletsData = await getWallets();
          const walletsArr = (walletsData && walletsData.data && walletsData.data.data) || [];
          setWallets(walletsArr);
        } else {
          setTransferError(processRes.message || 'Crypto transfer processing failed.');
        }
      } else {
        setTransferError(initiateRes.message || 'Crypto transfer initiation failed.');
      }
    } catch (error) {
      console.error('Crypto transfer error:', error);
      setTransferError('Failed to process crypto transfer. Please try again.');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setAmount('');
    setDestinationAddress('');
    setNetwork('TRC20');
    setDescription('');
    setPin('');
    setTransferError('');
    setTransferResult(null);
  };

  const handleGoBack = () => {
    navigate('/wallets');
  };

  if (loading) return <Preloader fullScreen={true} message="Loading wallets..." />;
  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-3 sm:p-4">
      <div className="fintech-card p-6 sm:p-8 text-center max-w-sm sm:max-w-md">
        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-danger-100 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
          <ArrowBackIcon className="text-danger-600 text-xl sm:text-2xl" />
        </div>
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Failed to Load Wallets</h2>
        <p className="text-gray-600 mb-4 text-sm sm:text-base">{error}</p>
        <button onClick={handleGoBack} className="btn-primary text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3">
          Go Back
        </button>
      </div>
    </div>
  );

  // Check if source wallet is a crypto wallet
  if (sourceWallet && !isCryptoWallet(sourceWallet)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-3 sm:p-4">
        <div className="fintech-card p-6 sm:p-8 text-center max-w-sm sm:max-w-md">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-warning-100 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <ArrowBackIcon className="text-warning-600 text-xl sm:text-2xl" />
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Crypto Transfer Only</h2>
          <p className="text-gray-600 mb-4 text-sm sm:text-base">Only crypto wallets can be used for crypto transfers</p>
          <button onClick={handleGoBack} className="btn-primary text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-200/20 to-primary-300/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-200/20 to-indigo-300/20 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-white/80 backdrop-blur-xl border-b border-white/20 px-3 sm:px-4 md:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
            <button 
              onClick={() => setSidebarOpen && setSidebarOpen(true)} 
              className="block xl:hidden text-gray-600 hover:text-gray-900 hover:bg-gray-100/50 rounded-xl p-1.5 sm:p-2 transition-all duration-200"
            >
              <MenuIcon className="text-xl sm:text-2xl" />
            </button>
            <button 
              onClick={handleGoBack}
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-100/50 rounded-xl p-1.5 sm:p-2 transition-all duration-200"
            >
              <ArrowBackIcon className="text-xl sm:text-2xl" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent truncate">
                Send Crypto
              </h1>
              <p className="text-gray-600 text-xs sm:text-sm md:text-base truncate">Send crypto to external addresses</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8">
        <div className="max-w-4xl mx-auto">
          
          {/* Source Wallet Card */}
          {sourceWallet && (
            <div className="fintech-card p-4 sm:p-6 mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Source Wallet</h2>
              <div className="flex items-center gap-3 sm:gap-4">
                <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center font-bold text-lg sm:text-xl shadow-sm ${
                  getCurrencyCode(sourceWallet) === 'NGN' ? 'bg-green-100 text-green-700' :
                  getCurrencyCode(sourceWallet) === 'USD' ? 'bg-blue-100 text-blue-700' :
                  getCurrencyCode(sourceWallet) === 'USDT' ? 'bg-green-100 text-green-700' :
                  getCurrencyCode(sourceWallet) === 'GBP' ? 'bg-purple-100 text-purple-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {getCurrencyCode(sourceWallet) === 'USDT' ? (
                    <SiTether className="text-lg sm:text-xl" />
                  ) : (
                    getFlag(getCurrencyCode(sourceWallet))
                  )}
                </div>
                <div className="flex-1">
                  <div className="text-sm sm:text-base font-semibold text-gray-900">
                    {sourceWallet.ownership_label || `${getCurrencyCode(sourceWallet)} Wallet`}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500">
                    {sourceWallet.wallet_number}
                  </div>
                  <div className="text-sm sm:text-base font-bold text-gray-900 mt-1">
                    {getSymbol(getCurrencyCode(sourceWallet))}{parseFloat(sourceWallet.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Transfer Steps */}
          <div className="fintech-card p-4 sm:p-6">
            <Stepper activeStep={activeStep} className="mb-6 sm:mb-8">
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step Content */}
            <div className="min-h-[400px]">
              {activeStep === 0 && (
                <div className="space-y-4">
                  <Typography variant="h6" className="mb-4">Enter Transfer Details</Typography>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{sourceWallet ? sourceWallet.currency?.code : ''}</InputAdornment>,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Destination Address"
                    value={destinationAddress}
                    onChange={(e) => setDestinationAddress(e.target.value)}
                    sx={{ mb: 3 }}
                    placeholder="0x..."
                  />
                  <TextField
                    select
                    fullWidth
                    label="Network"
                    value={network}
                    onChange={(e) => setNetwork(e.target.value)}
                    sx={{ mb: 3 }}
                  >
                    {sourceWallet?.currency?.code === 'USDT' && (
                      <>
                        <MenuItem value="TRC20">TRC20 (Tron)</MenuItem>
                        <MenuItem value="ERC20">ERC20 (Ethereum)</MenuItem>
                      </>
                    )}
                    {sourceWallet?.currency?.code === 'USDC' && (
                      <MenuItem value="POL">POL (Polygon)</MenuItem>
                    )}
                    {!['USDT', 'USDC'].includes(sourceWallet?.currency?.code) && (
                      <>
                        <MenuItem value="TRC20">TRC20 (Tron)</MenuItem>
                        <MenuItem value="ERC20">ERC20 (Ethereum)</MenuItem>
                        <MenuItem value="POL">POL (Polygon)</MenuItem>
                      </>
                    )}
                  </TextField>
                  <TextField
                    fullWidth
                    label="Description (optional)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    sx={{ mb: 3 }}
                    multiline
                    rows={3}
                  />
                </div>
              )}

              {activeStep === 1 && (
                <div className="space-y-4">
                  <Typography variant="h6" className="mb-4">Confirm Transfer</Typography>
                  
                  <Paper elevation={1} sx={{ p: 3, mb: 3, background: '#f8fafc' }}>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">From:</span>
                        <span className="font-semibold">{sourceWallet?.wallet_number} ({sourceWallet?.currency?.code})</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-semibold">{amount} {sourceWallet?.currency?.code}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">To:</span>
                        <span className="font-semibold break-all">{destinationAddress}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Network:</span>
                        <span className="font-semibold">{network}</span>
                      </div>
                      {description && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Description:</span>
                          <span className="font-semibold">{description}</span>
                        </div>
                      )}
                    </div>
                  </Paper>

                  <TextField
                    fullWidth
                    label="Transaction PIN"
                    type={showPin ? 'text' : 'password'}
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    sx={{ mb: 3 }}
                    inputProps={{ maxLength: 4 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPin(!showPin)}
                            edge="end"
                          >
                            {showPin ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              )}

              {activeStep === 2 && transferResult && (
                <div className="space-y-4">
                  <Typography variant="h6" color="success.main" className="mb-4">Transfer Initiated!</Typography>
                  
                  <Paper elevation={1} sx={{ p: 3, mb: 3, background: '#e8f5e8' }}>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Transaction ID:</span>
                        <span className="font-semibold">{transferResult.reference_number || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-semibold">{amount} {sourceWallet?.currency?.code}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Network:</span>
                        <span className="font-semibold">{network}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span className="font-semibold text-green-600">Processing</span>
                      </div>
                    </div>
                  </Paper>
                </div>
              )}

              {transferError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {transferError}
                </Alert>
              )}
            </div>

            {/* Step Actions */}
            <div className="flex justify-between mt-6 sm:mt-8">
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Back
              </Button>
              <div>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleReset}
                  >
                    Send Another
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={activeStep === 1 ? handleTransfer : handleNext}
                    disabled={transferLoading || (activeStep === 0 && (!amount || Number(amount) <= 0)) || (activeStep === 1 && !pin)}
                  >
                    {transferLoading ? <CircularProgress size={20} /> : (activeStep === 1 ? 'Send Crypto' : 'Next')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Snackbar */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={3000} 
        onClose={() => setSnackbarOpen(false)} 
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }} elevation={6} variant="filled">
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </div>
  );
} 